import { useEffect, useState } from 'react';

import { getApiBaseUrl } from './api-url';
import { getOfflineQueueCounts, subscribeToOfflineStore } from './offline-store';

export type CloudStatus = 'online' | 'degraded' | 'offline';

export interface OfflineStatus {
  isOnline: boolean;
  cloudStatus: CloudStatus;
  pendingCount: number;
  queuedCount: number;
  syncingCount: number;
  failedCount: number;
  conflictCount: number;
}

function browserOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}

let cloudStatusCache: { status: CloudStatus; checkedAt: number } | null = null;
let cloudStatusRequest: Promise<CloudStatus> | null = null;
const CLOUD_STATUS_CACHE_MS = 10_000;

async function resolveCloudStatus(): Promise<CloudStatus> {
  if (!browserOnline()) return 'offline';
  const now = Date.now();
  if (cloudStatusCache && now - cloudStatusCache.checkedAt < CLOUD_STATUS_CACHE_MS) {
    return cloudStatusCache.status;
  }
  if (cloudStatusRequest) return cloudStatusRequest;

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 3_000);
  cloudStatusRequest = (async () => {
    const response = await fetch(`${getApiBaseUrl()}/health`, {
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!response.ok) return 'degraded';
    const payload = (await response.json()) as { status?: string };
    return payload.status === 'healthy' ? 'online' : 'degraded';
  })();

  try {
    const status = await cloudStatusRequest;
    cloudStatusCache = { status, checkedAt: Date.now() };
    return status;
  } catch {
    return 'offline';
  } finally {
    cloudStatusRequest = null;
    window.clearTimeout(timeout);
  }
}

export function useOfflineStatus(): OfflineStatus {
  const [status, setStatus] = useState<OfflineStatus>({
    isOnline: browserOnline(),
    cloudStatus: browserOnline() ? 'online' : 'offline',
    pendingCount: 0,
    queuedCount: 0,
    syncingCount: 0,
    failedCount: 0,
    conflictCount: 0,
  });

  useEffect(() => {
    let cancelled = false;

    const refresh = () => {
      Promise.all([getOfflineQueueCounts(), resolveCloudStatus()])
        .then(([counts, cloudStatus]) => {
          if (!cancelled) {
            setStatus({
              isOnline: cloudStatus === 'online',
              cloudStatus,
              pendingCount: counts.queued + counts.syncing + counts.failed + counts.conflict,
              queuedCount: counts.queued,
              syncingCount: counts.syncing,
              failedCount: counts.failed,
              conflictCount: counts.conflict,
            });
          }
        })
        .catch(() => {
          if (!cancelled) {
            const cloudStatus = browserOnline() ? 'degraded' : 'offline';
            setStatus((current) => ({
              ...current,
              cloudStatus,
              isOnline: false,
            }));
          }
        });
    };

    refresh();
    const unsubscribe = subscribeToOfflineStore(refresh);
    const interval = window.setInterval(refresh, 15_000);

    return () => {
      cancelled = true;
      unsubscribe();
      window.clearInterval(interval);
    };
  }, []);

  return status;
}
